import {STUser} from "api/organisationApi/types";
import mapxProjectApi from "api/projectApi";
import {STProject, STProjectForm, TScratchProjectForm} from "api/projectApi/types";
import mapxTargetListApi from "api/targetListApi";
import {ToastContent} from "components";
import {createBroadcastChannel} from "helpers/BroadcastChannel";
import {BROADCAST_CHANNELS} from "helpers/BroadcastChannel/BroadcastChannels";
import index from "helpers/CustomConfirmAlert";
import {successResponse} from "helpers/map";
import {Mixpanel} from "helpers/mixpanel";
import {TProjectFormData} from "mapx-components/Modals/ProjectCreateWithWorkflowsModal/ProjectForm/types";
import {TProjectForm} from "mapx-components/Modals/ProjectCreateWithWorkflowsModal/types";
import {toast} from "react-toastify";
import {
	duplicateProjectDone,
	duplicateProjectFail,
	duplicateProjectInit,
	duplicateProjectModal,
	editProjectModal,
	saveProjectViaModalDone,
	saveProjectViaModalFail,
	saveProjectViaModalInit,
	updateProjectDone,
	updateProjectFail,
	updateProjectInit,
} from "store/project-list/projectListActions";
import {setProjectAsync} from "store/project-list/projectListAsyncActions";
import {projectSelector, projectsSelector} from "store/project-list/projectListSelectors";
import {
	fetchTargetListCompanyIDs,
	getTargetListCompanies,
} from "store/target-list/targetListAsyncActions";
import {
	fetchTargetListCandidateIDs,
	getTargetListCandidates,
} from "store/target-list/targetListCandidatesAsyncActions";
import {
	detachedTargetListSelector,
	targetCandidatesPaginationSelector,
	targetCompaniesPaginationSelector,
	targetListCompaniesSelector,
} from "store/target-list/targetListSelectors";
import {userEmailSelector, userSelector} from "store/user/userSelectors";
import {TAppDispatch, TRootState} from "types";

export const mergeTargetlist =
	(newProject: STProject) => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		let detachedTargetList = detachedTargetListSelector(state);

		if (!detachedTargetList) {
			const response = await mapxTargetListApi.createDetachedTargetList();

			detachedTargetList = response?.data;
		}

		if (!detachedTargetList) {
			return newProject;
		}

		const selectedProjectInState = projectSelector(state);
		const companies = targetListCompaniesSelector(state);
		const companiesPagination = targetCompaniesPaginationSelector(state);
		const candidatesPagination = targetCandidatesPaginationSelector(state);

		if (
			!selectedProjectInState &&
			(companiesPagination?.count > 0 || candidatesPagination?.count > 0)
		) {
			index({
				yesButtonLabel: "Yes",
				noButtonLabel: "No",
				title: "Transfer Target List?",
				message: `Would you like to transfer the saved companies and people to this new project?`,
				handlePressYes: async () => {
					try {
						if (typeof newProject?.target_list_id === "number") {
							await mapxTargetListApi.mergeTargetLists(
								detachedTargetList.id,
								newProject.target_list_id,
							);

							newProject.companies = companies;
							newProject.companies_number = companiesPagination.count;
							newProject.candidates_number = candidatesPagination.count;

							if (companiesPagination.count) {
								dispatch(getTargetListCompanies());
								dispatch(fetchTargetListCompanyIDs());
							}

							if (candidatesPagination.count) {
								dispatch(getTargetListCandidates());
								dispatch(fetchTargetListCandidateIDs());
							}
						}

						return newProject;
					} catch (error) {
						console.log(error);
					}
				},
				afterClose: () => {
					return newProject;
				},
			});
		}

		return newProject;
	};

export const saveProjectForAI =
	(formData: TProjectForm) => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			const state = getState();

			dispatch(saveProjectViaModalInit());

			const userEmail = userEmailSelector(state);

			const response = await mapxProjectApi.saveProject(formData);

			if (successResponse(response, 201)) {
				const newProject = response.data;

				dispatch(saveProjectViaModalDone(newProject));

				dispatch(setProjectAsync(newProject));

				Mixpanel.track(`Project Created`, {
					name: `${newProject.name}`,
					pageTitle: `Project Create Modal`,
					url: window.location.pathname,
					distinct_id: userEmail,
				});

				return newProject;
			} else {
				return null;
			}
		} catch (error) {
			dispatch(saveProjectViaModalFail(error));

			return null;
		}
	};

export const saveProjectFromScratch =
	(formData: TScratchProjectForm) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			const state = getState();

			dispatch(saveProjectViaModalInit());

			const userEmail = userEmailSelector(state);

			const {user_ids, ...data} = formData;

			const response = await mapxProjectApi.saveProject(data);

			if (successResponse(response, 201)) {
				const newProject = (response?.data as STProject) || {};

				if (user_ids && user_ids?.length > 0) {
					await mapxProjectApi.shareProjectWithUsers(newProject.id, {user_ids});

					const response = await mapxProjectApi.getProjectUsers(newProject.id);
					newProject.users = response?.data?.users || newProject.users;
				}

				if (!newProject.target_list_id) {
					const response = await mapxTargetListApi.createTargetList({
						project_id: newProject.id,
					});
					newProject.target_list_id = response?.data?.id;
				}

				const newProjectWithData = await dispatch(mergeTargetlist(newProject));

				dispatch(saveProjectViaModalDone(newProjectWithData));

				dispatch(setProjectAsync(newProjectWithData));

				Mixpanel.track(`Project Created`, {
					name: `${newProject.name}`,
					pageTitle: `Project Create Modal`,
					url: window.location.pathname,
					distinct_id: userEmail,
				});

				return newProject;
			} else {
				return null;
			}
		} catch (error) {
			dispatch(saveProjectViaModalFail(error));

			return null;
		}
	};

export const updateProjectUsers =
	(projectID: number, targetUsers: STUser[]) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			const state = getState();
			const user = userSelector(state);

			dispatch(updateProjectInit());

			const response = await mapxProjectApi.getProjectUsers(projectID);
			const currentUsers = response?.data?.users || [];
			const currentIds = currentUsers.map((item: STUser) => Number(item.id));
			const targetIds = targetUsers.map((item: STUser) => Number(item.id));

			if (!targetIds.includes(user.user_id)) {
				targetIds.push(user.user_id);
			}

			const toAdd = targetIds.filter((id) => !currentIds.includes(id));
			const toRemove = currentIds.filter((id) => !targetIds.includes(id));

			if (toAdd.length > 0) {
				await mapxProjectApi.shareProjectWithUsers(projectID, {user_ids: toAdd});
			}

			if (toRemove.length > 0) {
				await mapxProjectApi.removeUserFromProject(projectID, {user_ids: toRemove});
			}

			const updatedResponse = await mapxProjectApi.getProjectUsers(projectID);
			const users = updatedResponse?.data?.users || [];

			const projects = projectsSelector(state);
			const activeProject = projectSelector(state);
			const project = projects.find((p) => p.id === projectID) || activeProject;

			if (project && project.id === projectID) {
				const updatedProject = {...project, users};
				dispatch(updateProjectDone(updatedProject));

				const {postMessage} = createBroadcastChannel<STProject>(BROADCAST_CHANNELS.PROJECT);
				postMessage(updatedProject);

				return updatedProject;
			}

			return null;
		} catch (error) {
			dispatch(updateProjectFail(error));

			return {error};
		}
	};

export const updateProject =
	(projectID: number, formData: TProjectFormData, displayNotification = true) =>
	async (dispatch: TAppDispatch) => {
		try {
			dispatch(updateProjectInit());

			const updatedForm: STProjectForm = {};

			if (formData.name) {
				updatedForm["name"] = formData.name;
			}

			if (formData.location) {
				updatedForm["location"] = formData.location.id as string;
			}

			if (formData.company !== undefined && formData.company !== null) {
				updatedForm["company_id"] = formData.company.id as number;
			} else if (formData.company !== undefined && formData.company === null) {
				updatedForm["company_id"] = null;
			}

			if (formData.target_job_title) {
				updatedForm["target_job_title"] = formData.target_job_title;
			}

			if (formData.is_tracking_enabled !== undefined) {
				updatedForm["is_tracking_enabled"] = formData.is_tracking_enabled;
			}

			const response = await mapxProjectApi.updateProject(projectID, updatedForm);

			if (successResponse(response, 200)) {
				const project = response.data as STProject;

				dispatch(updateProjectDone(project));

				const {postMessage} = createBroadcastChannel<STProject>(BROADCAST_CHANNELS.PROJECT);

				postMessage(project);

				if (displayNotification) {
					toast.success(ToastContent, {
						autoClose: 5000,
						closeOnClick: true,
						data: {
							title: "Project updated!",
							description: "Project changes saved successfully.",
						},
					});
				}

				dispatch(editProjectModal(false));
			}
		} catch (error) {
			dispatch(updateProjectFail(error));

			return {error};
		}
	};

export const duplicateProject =
	(parentProjectID: number, formData: TProjectFormData) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			dispatch(duplicateProjectInit());

			const data: STProjectForm = {};

			const state = getState();

			const user = userSelector(state);

			if (formData.name) {
				data["name"] = formData.name;
			}

			data["parent_project_id"] = parentProjectID;

			if (formData.location) {
				data["location"] = formData.location.id as string;
			}

			if (formData.company !== undefined && formData.company !== null) {
				data["company_id"] = formData.company.id as number;
			} else if (formData.company !== undefined && formData.company === null) {
				data["company_id"] = null;
			}

			const response = await mapxProjectApi.saveProject(data);

			if (successResponse(response, 201)) {
				const project = (response?.data as STProject) || {};

				if (formData.users && formData.users?.length > 0) {
					const userIds = [
						...formData.users.map((item: STUser) => Number(item.id)),
						user.user_id,
					];
					await mapxProjectApi.shareProjectWithUsers(project.id, {user_ids: userIds});

					const response = await mapxProjectApi.getProjectUsers(project.id);
					project.users = response?.data?.users || project.users;
				}

				dispatch(duplicateProjectDone(project));

				toast.success(ToastContent, {
					autoClose: 5000,
					closeOnClick: true,
					data: {
						title: "Project duplicated!",
						description: "Project duplicated successfully.",
					},
				});

				dispatch(duplicateProjectModal(false));
			}
		} catch (error) {
			dispatch(duplicateProjectFail(error));

			return {error};
		}
	};
